import React from 'react';
import { LanguageContext } from '../utilities/LocalizationModule';
import { Stack, Label, Separator, Persona, PersonaSize, Panel, PrimaryButton, TooltipHost, TooltipDelay } from '@fluentui/react';
import * as TopNavStyle from "../styles/TopNavigatorStyle"
import { useMsal } from '@azure/msal-react';
import { signInScopes } from '../utilities/authConfig';
import { getUserProfilePicture } from '../utilities/helpers/ApiHelper';
import { ThemeToggle } from './ThemeToggle';
import { labelHeadingStyle } from '../styles/PanelStyle';

interface TopNavigatorProps {
    userTenantName: string;
    signOut: () => void;
};

const TopNavigator: React.FC<TopNavigatorProps> = ({ userTenantName, signOut }: TopNavigatorProps) => {
    const languageStrings = React.useContext(LanguageContext);

    const [profilePic, setProfilePic] = React.useState("");
    const [profilePanelIsOpen, setProfilePanelIsOpen] = React.useState(false);

    const { instance, accounts } = useMsal();

    React.useEffect(() => {
        (async () => {
            const silentTokenRequest = {
                scopes: signInScopes,
                account: accounts[0]
            };
            try {
                const response = await instance.acquireTokenSilent(silentTokenRequest);
                const profileImageUrl: string = await getUserProfilePicture(response?.accessToken);
                if (profileImageUrl) {
                    setProfilePic(profileImageUrl);
                }
            } catch (error) {
                // Ignore
            }
        })();
    }, [accounts]);

    return (
        <>
            <Stack style={TopNavStyle.navContainerStyle} horizontal horizontalAlign="space-between" verticalAlign="center">
                <Stack.Item>
                    <Label styles={TopNavStyle.titleStyle}>
                        Docubird Admin Portal
                    </Label>
                </Stack.Item>
                <Stack.Item>
                    <TooltipHost content={`${languageStrings.SignedInUser}${accounts[0]?.name}`} delay={TooltipDelay.long} >
                        <Persona
                            styles={{ root: { paddingRight: 15, cursor: "pointer" } }}
                            imageUrl={profilePic}
                            text={accounts[0]?.name}
                            hidePersonaDetails
                            size={PersonaSize.size32}
                            onClick={() => { setProfilePanelIsOpen(true) }}
                        />
                    </TooltipHost>
                </Stack.Item>
            </Stack>
            <Panel
                isOpen={profilePanelIsOpen}
                closeButtonAriaLabel="Close"
                headerText={userTenantName}
                isHiddenOnDismiss={true}
                isLightDismiss={true}
                onDismiss={() => setProfilePanelIsOpen(false)}
            >
                {/* 8px-based spacing scale (matches the add-in settings panel): 8 around the
                    separators (every outer child is separator-adjacent, so this keeps them
                    symmetric and compact), header -> control 8 (Appearance is a section
                    header here, so it gets the add-in's 8px section-below, not the 4px
                    plain-label gap). */}
                <div style={{ marginTop: 8 }}>
                    <Stack tokens={{ childrenGap: 8 }}>
                        <Persona
                            imageUrl={profilePic}
                            text={accounts[0]?.name}
                            secondaryText={accounts[0]?.username}
                            size={PersonaSize.size40}
                        />
                        <Separator />
                        {/* Appearance lives in the profile panel — a personal
                            preference sits naturally beside the account. */}
                        <Stack tokens={{ childrenGap: 8 }}>
                            {/* Zero Fluent Label's default 5px vertical padding: it made the
                                gap below separator-1 5px larger than the other separator gaps
                                (uneven), and pushed header->control past the intended 8px. */}
                            <Label styles={{ root: [labelHeadingStyle.root, { padding: 0 }] }}>{languageStrings.Appearance}</Label>
                            <ThemeToggle />
                        </Stack>
                        <Separator />
                        <PrimaryButton
                            text={languageStrings.SignOut}
                            className="logout"
                            onClick={signOut}
                            allowDisabledFocus
                        />
                    </Stack>
                </div>
            </Panel>
        </>
    )
}

export default TopNavigator;